import React, { useState, useEffect } from 'react';
import { toast } from 'react-hot-toast';
import api from '../../utils/apiClient';
import { API_ENDPOINTS } from '../../config/api';
import { FaEthereum, FaPlus, FaEdit, FaTrash, FaCube } from 'react-icons/fa';
import ImageUploader from '../common/ImageUploader';

const AdminNFTCollections = () => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [currentCollection, setCurrentCollection] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    totalSupply: 1000,
    price: 0.08,
    currency: 'ETH',
    creator: 'BitSport',
    releaseDate: new Date().toISOString().split('T')[0],
    bannerImage: '/nft/collection-banner.jpg',
    logoImage: '/nft/collection-logo.png',
    revenueSplit: {
      royaltyWallet: '0xf3083532888EA3C13409D9a13d503d6530528350',
      royaltyPercentage: 13.4,
      adminWallet: '0x77a2e4631D4Ba32436D016004e02805dCb03660b'
    },
    status: 'draft'
  });

  // Fetch collections on component mount
  useEffect(() => {
    fetchCollections();
  }, []);

  // Fetch collections from API
  const fetchCollections = async () => {
    try {
      setLoading(true);
      console.log('Fetching collections from endpoint:', API_ENDPOINTS.admin.nft.collections);
      const response = await api.get(API_ENDPOINTS.admin.nft.collections);
      console.log('Collections response:', response);

      // Check if response is an array or has a data property
      const collectionsData = Array.isArray(response) ? response :
                             (response.data ? response.data : []);

      console.log('Setting collections state with:', collectionsData);
      setCollections(collectionsData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching NFT collections:', error);
      console.error('Error details:', error.message, error.response);
      toast.error('Failed to fetch NFT collections');
      setLoading(false);
    }
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      totalSupply: 1000,
      price: 0.08,
      currency: 'ETH',
      creator: 'BitSport',
      releaseDate: new Date().toISOString().split('T')[0],
      bannerImage: '/nft/collection-banner.jpg',
      logoImage: '/nft/collection-logo.png',
      revenueSplit: {
        royaltyWallet: '0xf3083532888EA3C13409D9a13d503d6530528350',
        royaltyPercentage: 13.4,
        adminWallet: '0x77a2e4631D4Ba32436D016004e02805dCb03660b'
      },
      status: 'draft'
    });
    setCurrentCollection(null);
  };

  // Open edit modal
  const handleEdit = (collection) => {
    setCurrentCollection(collection);
    setFormData({
      name: collection.name,
      description: collection.description,
      totalSupply: collection.totalSupply,
      price: collection.price,
      currency: collection.currency,
      creator: collection.creator,
      releaseDate: new Date(collection.releaseDate).toISOString().split('T')[0],
      bannerImage: collection.bannerImage,
      logoImage: collection.logoImage,
      revenueSplit: {
        royaltyWallet: collection.revenueSplit?.royaltyWallet || '0xf3083532888EA3C13409D9a13d503d6530528350',
        royaltyPercentage: collection.revenueSplit?.royaltyPercentage || 13.4,
        adminWallet: collection.revenueSplit?.adminWallet || '0x77a2e4631D4Ba32436D016004e02805dCb03660b'
      },
      status: collection.status
    });
    setShowCreateModal(true);
  };

  // Handle collection submission
  const handleSubmitCollection = async (e) => {
    e.preventDefault();

    try {
      // Handle numeric values
      const submitData = {
        ...formData,
        totalSupply: Number(formData.totalSupply),
        price: Number(formData.price),
        revenueSplit: {
          ...formData.revenueSplit,
          royaltyPercentage: Number(formData.revenueSplit.royaltyPercentage)
        }
      };

      console.log('Submitting collection data:', submitData);
      console.log('Using endpoint:', currentCollection ?
        API_ENDPOINTS.admin.nft.update(currentCollection._id) :
        API_ENDPOINTS.admin.nft.create);

      let response;
      if (currentCollection) {
        // Update existing collection
        response = await api.put(API_ENDPOINTS.admin.nft.update(currentCollection._id), submitData);
        console.log('Collection update response:', response);
        toast.success('Collection updated successfully');
      } else {
        // Create new collection
        response = await api.post(API_ENDPOINTS.admin.nft.create, submitData);
        console.log('Collection creation response:', response);
        toast.success('Collection created successfully');
      }

      // Add a small delay before fetching collections to ensure the server has processed the change
      setTimeout(() => {
        // Refresh collections
        fetchCollections();

        // Close modal and reset form
        setShowCreateModal(false);
        resetForm();
      }, 500);
    } catch (error) {
      console.error('Error submitting collection:', error);
      console.error('Error details:', error.message, error.response);
      toast.error('Failed to save collection');
    }
  };

  // Handle collection deletion
  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this collection? This action cannot be undone.')) {
      try {
        await api.delete(API_ENDPOINTS.admin.nft.delete(id));
        toast.success('Collection deleted successfully');
        fetchCollections();
      } catch (error) {
        console.error('Error deleting collection:', error);
        toast.error('Failed to delete collection');
      }
    }
  };

  // Filter collections based on search term
  const filteredCollections = collections && collections.length > 0
    ? collections.filter(collection =>
        collection.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        collection.description.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div>
      {/* Collections Section */}
      <div className="mb-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">NFT Collections</h2>
          <button
            onClick={() => {
              resetForm();
              setShowCreateModal(true);
            }}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200 flex items-center"
          >
            <FaPlus className="mr-2" /> Create Collection
          </button>
        </div>

        <div className="mb-4">
          <input
            type="text"
            placeholder="Search collections..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4">
            {filteredCollections.length === 0 ? (
              <div className="text-center py-10 text-gray-400">
                No collections found
              </div>
            ) : (
              filteredCollections.map(collection => (
                <div key={collection._id} className="bg-gray-800 rounded-lg p-4 border border-gray-700">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h3 className="text-lg font-semibold">{collection.name}</h3>
                      <p className="text-gray-400 text-sm">{collection.description}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(collection)}
                        className="px-3 py-1 bg-gray-700 hover:bg-gray-600 rounded-lg text-sm transition duration-200 flex items-center"
                      >
                        <FaEdit className="mr-1" /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(collection._id)}
                        className="px-3 py-1 bg-red-900 hover:bg-red-800 text-red-300 rounded-lg text-sm transition duration-200 flex items-center"
                      >
                        <FaTrash className="mr-1" /> Delete
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                    <div className="bg-gray-700 p-2 rounded-lg">
                      <p className="text-xs text-gray-400">Total Supply</p>
                      <p className="font-semibold">{collection.totalSupply}</p>
                    </div>
                    <div className="bg-gray-700 p-2 rounded-lg">
                      <p className="text-xs text-gray-400">Minted</p>
                      <p className="font-semibold">{collection.mintedCount}</p>
                    </div>
                    <div className="bg-gray-700 p-2 rounded-lg">
                      <p className="text-xs text-gray-400">Price</p>
                      <p className="font-semibold flex items-center">
                        {collection.price} <FaEthereum className="ml-1" />
                      </p>
                    </div>
                    <div className="bg-gray-700 p-2 rounded-lg">
                      <p className="text-xs text-gray-400">Status</p>
                      <p className={`font-semibold ${
                        collection.status === 'active' ? 'text-green-400' :
                        collection.status === 'paused' ? 'text-yellow-400' :
                        collection.status === 'completed' ? 'text-blue-400' : 'text-gray-400'
                      }`}>
                        {collection.status.charAt(0).toUpperCase() + collection.status.slice(1)}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">
                {currentCollection ? 'Edit Collection' : 'Create Collection'}
              </h2>
              <button
                onClick={() => setShowCreateModal(false)}
                className="text-gray-400 hover:text-white"
              >
                &times;
              </button>
            </div>

            <form onSubmit={handleSubmitCollection}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Creator</label>
                  <input
                    type="text"
                    name="creator"
                    value={formData.creator}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows="3"
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Total Supply</label>
                  <input
                    type="number"
                    name="totalSupply"
                    value={formData.totalSupply}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Price</label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleInputChange}
                    required
                    min="0"
                    step="0.01"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Currency</label>
                  <select
                    name="currency"
                    value={formData.currency}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="ETH">ETH</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <ImageUploader
                    imageUrl={formData.bannerImage}
                    onImageUploaded={(imageUrl) => {
                      setFormData({
                        ...formData,
                        bannerImage: imageUrl
                      });
                    }}
                    uploadEndpoint={API_ENDPOINTS.admin.nft.uploadCollectionBanner}
                    label="Collection Banner Image"
                    aspectRatio="16/9"
                  />
                  <p className="text-xs text-gray-400 mt-1">Recommended size: 1200x400px</p>
                </div>
                <div>
                  <ImageUploader
                    imageUrl={formData.logoImage}
                    onImageUploaded={(imageUrl) => {
                      setFormData({
                        ...formData,
                        logoImage: imageUrl
                      });
                    }}
                    uploadEndpoint={API_ENDPOINTS.admin.nft.uploadCollectionLogo}
                    label="Collection Logo Image"
                    aspectRatio="1/1"
                  />
                  <p className="text-xs text-gray-400 mt-1">Recommended size: 400x400px</p>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Release Date</label>
                <input
                  type="date"
                  name="releaseDate"
                  value={formData.releaseDate}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Status</label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="draft">Draft</option>
                  <option value="active">Active</option>
                  <option value="paused">Paused</option>
                  <option value="completed">Completed</option>
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  <strong>Important:</strong> Only collections with status "active" will appear on the frontend.
                </p>
              </div>

              <h3 className="text-lg font-semibold mb-2">Revenue Split</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Royalty Wallet</label>
                  <input
                    type="text"
                    name="revenueSplit.royaltyWallet"
                    value={formData.revenueSplit.royaltyWallet}
                    onChange={handleInputChange}
                    required
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-1">Royalty Percentage</label>
                  <input
                    type="number"
                    name="revenueSplit.royaltyPercentage"
                    value={formData.revenueSplit.royaltyPercentage}
                    onChange={handleInputChange}
                    required
                    min="0"
                    max="100"
                    step="0.1"
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-400 mb-1">Admin Wallet</label>
                <input
                  type="text"
                  name="revenueSplit.adminWallet"
                  value={formData.revenueSplit.adminWallet}
                  onChange={handleInputChange}
                  required
                  className="w-full p-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end space-x-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition duration-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition duration-200 flex items-center"
                >
                  <FaCube className="mr-2" />
                  {currentCollection ? 'Update Collection' : 'Create Collection'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNFTCollections;
